import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, KeyRound, Copy, Trash2, Plus, Lock, Building2 } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [mfa, setMfa] = useState(null); // {otpauth_url, secret}
  const [otp, setOtp] = useState("");
  const [msg, setMsg] = useState("");
  const [keys, setKeys] = useState([]);
  const [showKey, setShowKey] = useState(null);
  const [newKey, setNewKey] = useState({ name: "", organization: "", scopes: ["read:cases"] });

  const loadKeys = async () => {
    if (user?.role !== "admin") return;
    try { const { data } = await api.get("/api-keys"); setKeys(data); }
    catch (e) { console.warn("api-keys fetch failed", e); }
  };
  useEffect(() => { loadKeys(); }, [user]);

  const setupMfa = async () => { const { data } = await api.post("/auth/mfa/setup"); setMfa(data); setMsg(""); };
  const verifyMfa = async () => {
    try { await api.post("/auth/mfa/verify", { otp }); setMsg("MFA enabled"); setMfa(null); setOtp(""); refresh(); }
    catch (e) { setMsg(e.response?.data?.detail || "Invalid code"); }
  };
  const disableMfa = async () => { await api.post("/auth/mfa/disable"); setMsg("MFA disabled"); refresh(); };

  const createKey = async () => {
    if (!newKey.name || !newKey.organization) return;
    const { data } = await api.post("/api-keys", newKey);
    setShowKey(data.key);
    setNewKey({ name: "", organization: "", scopes: ["read:cases"] });
    loadKeys();
  };
  const revokeKey = async (id) => { await api.post(`/api-keys/${id}/revoke`); loadKeys(); };

  const toggleScope = (s) => setNewKey(n => ({...n, scopes: n.scopes.includes(s) ? n.scopes.filter(x=>x!==s) : [...n.scopes, s]}));

  return (
    <div className="p-8 md:p-10 max-w-[1200px] mx-auto space-y-10">
      <div>
        <div className="overline">Account</div>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight mt-2">Security & Settings</h1>
      </div>

      {/* MFA */}
      <PasswordChange />
      <ProfileEditor />
      {user?.role === "admin" && <OrgSettings />}
      <section className="border border-slate-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-[#0033A0]" /><h2 className="font-heading font-extrabold text-2xl">Two-factor authentication</h2></div>
            <p className="text-sm text-slate-500 mt-1">TOTP via Authenticator app (Google Authenticator, 1Password, Authy).</p>
          </div>
          <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${user?.mfa_enabled ? "bg-[#16A34A] text-white" : "bg-slate-400 text-white"}`}>
            {user?.mfa_enabled ? "Enabled" : "Off"}
          </span>
        </div>
        {!user?.mfa_enabled && !mfa && (
          <button data-testid="mfa-setup" onClick={setupMfa} className="mt-4 bg-[#0033A0] text-white px-5 py-3 hover:bg-slate-900">Setup MFA</button>
        )}
        {mfa && (
          <div className="mt-6 grid md:grid-cols-[220px_1fr] gap-6 items-start">
            <div className="border border-slate-200 p-3 inline-block"><QRCodeSVG value={mfa.otpauth_url} size={200} /></div>
            <div>
              <div className="overline mb-1">Or enter manually</div>
              <div className="font-mono text-xs break-all border border-slate-200 p-2">{mfa.secret}</div>
              <div className="overline block mt-4 mb-1">6-digit code</div>
              <input data-testid="mfa-otp" value={otp} onChange={e => setOtp(e.target.value)} className="border border-slate-300 p-3 font-mono w-40" />
              <div className="mt-3">
                <button data-testid="mfa-verify" onClick={verifyMfa} className="bg-[#0033A0] text-white px-5 py-3 hover:bg-slate-900">Verify & enable</button>
              </div>
            </div>
          </div>
        )}
        {user?.mfa_enabled && <button onClick={disableMfa} className="mt-4 border border-red-300 text-red-600 px-5 py-3 hover:bg-red-600 hover:text-white">Disable MFA</button>}
        {msg && <div className="mt-3 text-sm text-slate-700">{msg}</div>}
      </section>

      {/* API Keys */}
      {user?.role === "admin" && (
        <section className="border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-2"><KeyRound size={18} className="text-[#0033A0]" /><h2 className="font-heading font-extrabold text-2xl">Public API Keys</h2></div>
          <p className="text-sm text-slate-500">Grant partner and government systems programmatic read access via <code className="font-mono">/api/gateway/v1/*</code> with header <code className="font-mono">X-API-Key</code>.</p>

          <div className="mt-6 border border-slate-200 p-4 bg-slate-50 grid md:grid-cols-4 gap-3">
            <input placeholder="Key name" data-testid="key-name" value={newKey.name} onChange={e => setNewKey({...newKey, name: e.target.value})} className="border border-slate-300 p-2 text-sm" />
            <input placeholder="Organization" data-testid="key-org" value={newKey.organization} onChange={e => setNewKey({...newKey, organization: e.target.value})} className="border border-slate-300 p-2 text-sm" />
            <div className="flex gap-2 flex-wrap text-xs items-center">
              {["read:cases","write:cases","read:analytics"].map(s => (
                <label key={s} className="flex items-center gap-1 border border-slate-200 px-2 py-1 cursor-pointer bg-white">
                  <input type="checkbox" checked={newKey.scopes.includes(s)} onChange={() => toggleScope(s)} />
                  <span className="font-mono">{s}</span>
                </label>
              ))}
            </div>
            <button data-testid="create-key" onClick={createKey} className="bg-[#0033A0] text-white px-4 py-2 flex items-center gap-2 hover:bg-slate-900"><Plus size={14} /> Generate</button>
          </div>

          {showKey && (
            <div className="mt-4 border border-amber-300 bg-amber-50 p-4 text-sm">
              <div className="font-semibold">Copy this key now — you won't see the full value again.</div>
              <div className="mt-2 flex items-center gap-3">
                <code className="font-mono break-all">{showKey}</code>
                <button onClick={() => { navigator.clipboard.writeText(showKey); }} className="border border-slate-300 px-2 py-1 text-xs flex items-center gap-1"><Copy size={12}/> Copy</button>
                <button onClick={() => setShowKey(null)} className="text-xs text-slate-500 underline">Dismiss</button>
              </div>
            </div>
          )}

          <div className="mt-6 border border-slate-200">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200">
                <th className="p-3 text-left overline">Name</th><th className="p-3 text-left overline">Org</th>
                <th className="p-3 text-left overline">Scopes</th><th className="p-3 text-left overline">Usage</th>
                <th className="p-3 text-left overline">Status</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id} className="border-b border-slate-100">
                    <td className="p-3 font-semibold">{k.name}</td>
                    <td className="p-3">{k.organization}</td>
                    <td className="p-3 font-mono text-xs">{(k.scopes||[]).join(", ")}</td>
                    <td className="p-3 font-mono text-xs">{k.usage_count || 0}</td>
                    <td className="p-3">{k.revoked ? <span className="bg-slate-500 text-white px-2 py-1 text-[10px] uppercase">Revoked</span> : <span className="bg-[#16A34A] text-white px-2 py-1 text-[10px] uppercase">Active</span>}</td>
                    <td className="p-3">{!k.revoked && <button onClick={() => revokeKey(k.id)} className="text-red-600 hover:text-red-800"><Trash2 size={14}/></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <a href="/api/admin/backup" onClick={async (e) => {
              e.preventDefault();
              const token = localStorage.getItem("icos_token");
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backup`;
              const r = await fetch(url, { headers: token ? {Authorization: `Bearer ${token}`}: {}, credentials: "include" });
              const blob = new Blob([JSON.stringify(await r.json(), null, 2)], { type: "application/json" });
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `icos-backup-${new Date().toISOString().slice(0,10)}.json`; a.click();
            }} data-testid="backup-btn" className="inline-block border border-slate-300 px-5 py-3 text-sm hover:bg-slate-900 hover:text-white">
              Download system backup (JSON)
            </a>
          </div>
        </section>
      )}
    </div>
  );
}

function PasswordChange() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [msg, setMsg] = useState(""); const [err, setErr] = useState(""); const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setMsg("");
    if (form.next !== form.confirm) return setErr("Passwords do not match");
    if (form.next.length < 8) return setErr("New password must be at least 8 characters");
    setSaving(true);
    try {
      await api.post("/auth/password", { current_password: form.current, new_password: form.next });
      setMsg("Password rotated successfully. Please use the new password next time you sign in.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };
  return (
    <section className="border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-2"><Lock size={18} className="text-[#0033A0]" /><h2 className="font-heading font-extrabold text-2xl">Rotate password</h2></div>
      <p className="text-sm text-slate-500">Rotate your account password. This is critical for the seeded admin account after first login.</p>
      <form onSubmit={submit} className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <input data-testid="pw-current" type="password" placeholder="Current password" required value={form.current} onChange={e => setForm({...form, current: e.target.value})} className="border border-slate-300 p-3 font-mono text-sm focus:border-[#0033A0] focus:outline-none" />
        <input data-testid="pw-new" type="password" placeholder="New password (min 8)" required value={form.next} onChange={e => setForm({...form, next: e.target.value})} className="border border-slate-300 p-3 font-mono text-sm focus:border-[#0033A0] focus:outline-none" />
        <input data-testid="pw-confirm" type="password" placeholder="Confirm new password" required value={form.confirm} onChange={e => setForm({...form, confirm: e.target.value})} className="border border-slate-300 p-3 font-mono text-sm focus:border-[#0033A0] focus:outline-none" />
        <div className="md:col-span-3 flex items-center gap-3">
          <button data-testid="pw-submit" disabled={saving} className="bg-[#0033A0] text-white px-5 py-3 hover:bg-slate-900 disabled:opacity-60">{saving ? "Saving…" : "Rotate password"}</button>
          {msg && <span className="text-sm text-[#16A34A]">{msg}</span>}
          {err && <span className="text-sm text-red-700">{err}</span>}
        </div>
      </form>
    </section>
  );
}

function ProfileEditor() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", organization: user?.organization || "" });
  const [msg, setMsg] = useState(""); const [saving, setSaving] = useState(false);
  useEffect(() => { setForm({ name: user?.name || "", organization: user?.organization || "" }); }, [user]);
  const submit = async (e) => {
    e.preventDefault(); setMsg(""); setSaving(true);
    try { await api.patch("/auth/me", form); await refresh(); setMsg("Profile updated"); }
    catch (e) { setMsg(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };
  return (
    <section className="border border-slate-200 p-6">
      <h2 className="font-heading font-extrabold text-2xl">Profile</h2>
      <p className="text-sm text-slate-500">Your display name and organisation attribution across audit logs and referrals.</p>
      <form onSubmit={submit} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="overline block mb-1">Full name</label>
          <input data-testid="profile-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border border-slate-300 p-3 focus:border-[#0033A0] focus:outline-none" />
        </div>
        <div><label className="overline block mb-1">Organisation</label>
          <input data-testid="profile-org" value={form.organization} onChange={e => setForm({...form, organization: e.target.value})} className="w-full border border-slate-300 p-3 focus:border-[#0033A0] focus:outline-none" />
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <button data-testid="profile-submit" disabled={saving} className="bg-[#0033A0] text-white px-5 py-3 hover:bg-slate-900">{saving ? "Saving…" : "Save profile"}</button>
          {msg && <span className="text-sm text-slate-700">{msg}</span>}
        </div>
      </form>
    </section>
  );
}

function OrgSettings() {
  const [form, setForm] = useState({ organization_name: "", country: "", contact_email: "", support_phone: "", default_locale: "en", logo_url: "", favicon_url: "" });
  const [msg, setMsg] = useState(""); const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/org-settings").then(r => {
      const d = r.data || {}; setForm(f => ({ ...f, ...d }));
    }).catch(e => console.warn("org-settings fetch failed", e));
  }, []);
  const submit = async (e) => {
    e.preventDefault(); setMsg(""); setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([_, v]) => v !== "" && v != null));
      await api.put("/admin/org-settings", payload); setMsg("Organisation settings saved");
    } catch (e) { setMsg(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };
  return (
    <section className="border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-2"><Building2 size={18} className="text-[#0033A0]" /><h2 className="font-heading font-extrabold text-2xl">Organisation</h2></div>
      <p className="text-sm text-slate-500">These values appear on the donor portal, exported reports, and outbound notifications.</p>
      <form onSubmit={submit} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="overline block mb-1">Organisation name</label>
          <input data-testid="org-name" value={form.organization_name || ""} onChange={e => setForm({...form, organization_name: e.target.value})} className="w-full border border-slate-300 p-3 focus:border-[#0033A0] focus:outline-none" />
        </div>
        <div><label className="overline block mb-1">Country</label>
          <input data-testid="org-country" value={form.country || ""} onChange={e => setForm({...form, country: e.target.value})} className="w-full border border-slate-300 p-3 focus:border-[#0033A0] focus:outline-none" placeholder="e.g. Nigeria" />
        </div>
        <div><label className="overline block mb-1">Contact email</label>
          <input data-testid="org-email" type="email" value={form.contact_email || ""} onChange={e => setForm({...form, contact_email: e.target.value})} className="w-full border border-slate-300 p-3 focus:border-[#0033A0] focus:outline-none font-mono text-sm" />
        </div>
        <div><label className="overline block mb-1">Support phone</label>
          <input data-testid="org-phone" value={form.support_phone || ""} onChange={e => setForm({...form, support_phone: e.target.value})} className="w-full border border-slate-300 p-3 focus:border-[#0033A0] focus:outline-none font-mono text-sm" />
        </div>
        <div><label className="overline block mb-1">Default locale</label>
          <select data-testid="org-locale" value={form.default_locale || "en"} onChange={e => setForm({...form, default_locale: e.target.value})} className="w-full border border-slate-300 p-3 focus:border-[#0033A0] focus:outline-none">
            <option value="en">English</option><option value="ha">Hausa</option><option value="yo">Yoruba</option><option value="ig">Igbo</option><option value="pcm">Nigerian Pidgin</option>
          </select>
        </div>
        <div><label className="overline block mb-1">Logo URL</label>
          <input data-testid="org-logo" value={form.logo_url || ""} onChange={e => setForm({...form, logo_url: e.target.value})} className="w-full border border-slate-300 p-3 focus:border-[#0033A0] focus:outline-none font-mono text-sm" placeholder="https://…" />
        </div>
        <div><label className="overline block mb-1">Favicon URL</label>
          <input data-testid="org-favicon" value={form.favicon_url || ""} onChange={e => setForm({...form, favicon_url: e.target.value})} className="w-full border border-slate-300 p-3 focus:border-[#0033A0] focus:outline-none font-mono text-sm" placeholder="https://… (leave blank to reuse logo)" />
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <button data-testid="org-submit" disabled={saving} className="bg-[#0033A0] text-white px-5 py-3 hover:bg-slate-900">{saving ? "Saving…" : "Save organisation"}</button>
          {msg && <span className="text-sm text-slate-700">{msg}</span>}
        </div>
      </form>
    </section>
  );
}
